/**
 * Auth selectors — memoized via reselect.
 * Selectors operate on the auth slice of RootState.
 */

import { createSelector } from 'reselect';
import type { AuthState, AuthUser } from './ducks';

// ─── Types ────────────────────────────────────────────────

interface StateWithAuth {
  auth: AuthState;
  [key: string]: unknown;
}

// ─── Base Selector ────────────────────────────────────────

export const selectAuthState = (state: StateWithAuth): AuthState => state.auth;

// ─── Derived Selectors ────────────────────────────────────

export const selectUser = createSelector(
  selectAuthState,
  (auth): AuthUser | null => auth.user
);

export const selectCurrentUser = selectUser;

export const selectIsLoading = createSelector(
  selectAuthState,
  (auth): boolean => auth.isLoading
);

export const selectAuthError = createSelector(
  selectAuthState,
  (auth): string | null => auth.error
);

export const selectUserId = createSelector(
  selectUser,
  (user): string | null => user?.id ?? null
);

export const selectUserEmail = createSelector(
  selectUser,
  (user): string | null => user?.email ?? null
);

export const selectUserName = createSelector(
  selectUser,
  (user): string | null => user?.display_name ?? null
);

export const selectUserRole = createSelector(
  selectUser,
  (user): string | undefined => user?.role
);

export const selectIsAdmin = createSelector(
  selectUser,
  (user): boolean => user?.role === 'admin'
);

export const selectFamilyView = createSelector(
  selectAuthState,
  (auth): boolean => auth.familyView
);

// Convenience: true once auth boot has settled (loading=false).
// ProtectedRoute uses this to decide whether to show Loading or
// proceed to render/redirect.
export const selectAuthSettled = createSelector(
  selectAuthState,
  (auth): boolean => !auth.isLoading
);

export const selectIsAuthenticated = createSelector(
  selectAuthState,
  (auth): boolean => auth.user !== null
);
